import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FileCheck, Filter, Plus, Search } from "lucide-react";
import { api, formatApiErrorDetail, useAuth } from "../../lib/auth";
import { useCapabilities } from "../../lib/useCapabilities";

const CONTENT_CLASSES = [
  "CMS_CONTENT",
  "DOMAIN_FOUNDATION",
  "BTC_KNOWLEDGE",
  "JOURNEY_DEFINITION",
  "ECOSYSTEM_ENTITY",
];
const LIFECYCLE_STATES = ["DRAFT", "REVIEW", "APPROVED", "REJECTED"];
const LANGUAGES = ["en", "es", "fr", "pt", "de", "it", "ar", "zh"];
const PROVENANCE = [
  "BTC_PROVIDED", "BTC_APPROVED",
  "PARTNER_SUPPORTED", "NOT_CURRENTLY_AVAILABLE",
];
const AVAILABILITY = [
  "CURRENTLY_AVAILABLE", "IN_DEVELOPMENT",
  "NOT_CURRENTLY_AVAILABLE", "FUTURE_ASPIRATIONAL",
];

const STATE_STYLES = {
  DRAFT: "bg-slate-100 text-slate-700",
  REVIEW: "bg-amber-100 text-amber-800",
  APPROVED: "bg-emerald-100 text-emerald-800",
  REJECTED: "bg-rose-100 text-rose-800",
};

export default function ContentGovernance() {
  const { user } = useAuth();
  const { caps } = useCapabilities();
  // BTC3 Foundation completion · Gate 3 — capability-driven create
  // visibility.  Backend `POST /content-governance` remains the
  // sole authorization gate.
  const canCreate = !!(caps.content_operations || caps.ecosystem_entity_create);

  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");
  const [filters, setFilters] = useState({
    content_class: "",
    lifecycle_state: "",
    language: "",
    content_key: "",
  });
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    content_class: "CMS_CONTENT",
    content_key: "",
    source_version: "",
    language: "en",
    provenance: "BTC_PROVIDED",
    availability: "NOT_CURRENTLY_AVAILABLE",
    availability_verified_at: "",
  });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setErr("");
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v && k !== "content_key") params[k] = v;
      });
      const { data } = await api.get("/ea2/governance", { params });
      let rows = data.items || [];
      if (filters.content_key) {
        const q = filters.content_key.toLowerCase();
        rows = rows.filter((r) => (r.content_key || "").toLowerCase().includes(q));
      }
      setItems(rows);
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load records.");
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const submitCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setErr("");
    try {
      const payload = { ...form };
      if (!payload.availability_verified_at) delete payload.availability_verified_at;
      await api.post("/ea2/governance", payload);
      setShowCreate(false);
      setForm((f) => ({ ...f, content_key: "", source_version: "", availability_verified_at: "" }));
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Create failed.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="content-governance-page">
      <div className="flex items-start gap-3">
        <FileCheck className="w-6 h-6 text-slate-500" />
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-slate-900">Content Governance</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">
            Internal governance workflow for BTC's authored content. This surface
            records provenance, availability, accessibility and inclusive-review
            metadata against exact content versions. It is <strong>not</strong> a
            publishing surface — approving a record does not make content
            patient-visible, groundable, or personalized.
          </p>
        </div>
        {canCreate && (
          <button
            data-testid="cg-create-toggle"
            onClick={() => setShowCreate((s) => !s)}
            className="btc-btn-primary text-sm inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> {showCreate ? "Cancel" : "New governance record"}
          </button>
        )}
      </div>

      {err && (
        <div className="border border-rose-300 bg-rose-50 text-rose-800 text-sm px-4 py-2 rounded" data-testid="cg-error">
          {err}
        </div>
      )}

      {showCreate && (
        <section className="bg-white rounded-xl border p-4" data-testid="cg-create-form">
          <form onSubmit={submitCreate} className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <label className="flex flex-col gap-1">
              <span className="text-slate-500">Content class</span>
              <select
                data-testid="cg-form-class"
                className="border rounded px-3 py-2"
                value={form.content_class}
                onChange={(e) => setForm({ ...form, content_class: e.target.value })}
              >
                {CONTENT_CLASSES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-slate-500">Content key</span>
              <input
                data-testid="cg-form-key" required
                className="border rounded px-3 py-2"
                placeholder="e.g. northstar.domain.wellness"
                value={form.content_key}
                onChange={(e) => setForm({ ...form, content_key: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-slate-500">Source version</span>
              <input
                data-testid="cg-form-version" required
                className="border rounded px-3 py-2" placeholder="v1"
                value={form.source_version}
                onChange={(e) => setForm({ ...form, source_version: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-slate-500">Language</span>
              <select
                data-testid="cg-form-language"
                className="border rounded px-3 py-2"
                value={form.language}
                onChange={(e) => setForm({ ...form, language: e.target.value })}
              >
                {LANGUAGES.map((l) => <option key={l}>{l}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-slate-500">Provenance</span>
              <select
                data-testid="cg-form-provenance"
                className="border rounded px-3 py-2"
                value={form.provenance}
                onChange={(e) => setForm({ ...form, provenance: e.target.value })}
              >
                {PROVENANCE.map((p) => <option key={p}>{p}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-slate-500">Availability</span>
              <select
                data-testid="cg-form-availability"
                className="border rounded px-3 py-2"
                value={form.availability}
                onChange={(e) => setForm({ ...form, availability: e.target.value })}
              >
                {AVAILABILITY.map((a) => <option key={a}>{a}</option>)}
              </select>
            </label>
            {["CURRENTLY_AVAILABLE", "IN_DEVELOPMENT"].includes(form.availability) && (
              <label className="flex flex-col gap-1 md:col-span-2">
                <span className="text-slate-500">Availability verified at (required to submit)</span>
                <input
                  data-testid="cg-form-verified-at"
                  type="datetime-local"
                  className="border rounded px-3 py-2"
                  value={form.availability_verified_at}
                  onChange={(e) => setForm({ ...form, availability_verified_at: e.target.value })}
                />
              </label>
            )}
            <div className="md:col-span-3">
              <button
                data-testid="cg-form-submit"
                disabled={creating}
                className="btc-btn-primary text-sm"
              >
                {creating ? "Creating…" : "Create in DRAFT"}
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="bg-white rounded-xl border p-4" data-testid="cg-filters">
        <div className="flex items-center gap-2 text-slate-500 text-sm mb-3">
          <Filter className="w-4 h-4" /> Filters
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-sm">
          <select
            data-testid="cg-filter-class"
            value={filters.content_class}
            onChange={(e) => setFilters({ ...filters, content_class: e.target.value })}
            className="border rounded px-3 py-2"
          >
            <option value="">All classes</option>
            {CONTENT_CLASSES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <select
            data-testid="cg-filter-state"
            value={filters.lifecycle_state}
            onChange={(e) => setFilters({ ...filters, lifecycle_state: e.target.value })}
            className="border rounded px-3 py-2"
          >
            <option value="">All states</option>
            {LIFECYCLE_STATES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <select
            data-testid="cg-filter-language"
            value={filters.language}
            onChange={(e) => setFilters({ ...filters, language: e.target.value })}
            className="border rounded px-3 py-2"
          >
            <option value="">All languages</option>
            {LANGUAGES.map((l) => <option key={l}>{l}</option>)}
          </select>
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-2 top-2.5" />
            <input
              data-testid="cg-filter-key"
              value={filters.content_key}
              onChange={(e) => setFilters({ ...filters, content_key: e.target.value })}
              placeholder="Content key contains…"
              className="border rounded pl-8 pr-3 py-2 w-full"
            />
          </div>
        </div>
      </section>

      <section className="bg-white rounded-xl border overflow-hidden" data-testid="cg-inventory">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div className="font-medium text-slate-900">Governance records</div>
          <div className="text-xs text-slate-500">{items.length} shown</div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left px-4 py-2">Class</th>
              <th className="text-left px-4 py-2">Content key</th>
              <th className="text-left px-4 py-2">Version</th>
              <th className="text-left px-4 py-2">Lang</th>
              <th className="text-left px-4 py-2">State</th>
              <th className="text-left px-4 py-2">Provenance</th>
              <th className="text-left px-4 py-2">Availability</th>
              <th className="text-left px-4 py-2">Next review</th>
              <th className="text-left px-4 py-2">Open</th>
            </tr>
          </thead>
          <tbody data-testid="cg-inventory-body">
            {items.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-500">No governance records match these filters.</td></tr>
            )}
            {items.map((r) => (
              <tr key={r.id} className="border-t" data-testid={`cg-row-${r.id}`}>
                <td className="px-4 py-3"><span className="text-xs bg-slate-100 px-2 py-0.5 rounded">{r.content_class}</span></td>
                <td className="px-4 py-3 font-medium text-slate-900 break-all">{r.content_key}</td>
                <td className="px-4 py-3 text-slate-600">{r.source_version} · v{r.version}</td>
                <td className="px-4 py-3">{r.language}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded text-xs ${STATE_STYLES[r.lifecycle_state] || "bg-slate-100"}`} data-testid={`cg-row-state-${r.id}`}>
                    {r.lifecycle_state}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-slate-600">{r.provenance}</td>
                <td className="px-4 py-3 text-xs text-slate-600">{r.availability}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{r.next_review_at ? r.next_review_at.slice(0, 10) : "—"}</td>
                <td className="px-4 py-3">
                  <Link to={`/ops/content-governance/${r.id}`} data-testid={`cg-row-open-${r.id}`} className="text-slate-700 underline">Open →</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
